// types
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { AppointmentImportSyncStatusResponse } from 'generated/index';

// utils
import i18n from 'config/i18n';
import { getParticipantPreview } from 'components/logs/components/SynchronisationLogs/SynchronisationLogs.utils';
import { getLocale } from 'utils/date';
import dayjs from 'dayjs';

const t = i18n.getFixedT(getLocale());

export type ImportSyncStatusLogRow = AppointmentImportSyncStatusResponse;

export const getImportSyncStatusRowId = (row: ImportSyncStatusLogRow): string =>
  row.appointmentEntityId || 'unknown';

export const getImportStatusLabel = (
  status: ImportSyncStatusLogRow['status']
): string => {
  switch (status) {
    case 'FAILED':
      return t('logsPortal.importSynchronisationLogs.stateValues.failed');
    case 'PARTIALLY_COMPLETE':
      return t(
        'logsPortal.importSynchronisationLogs.stateValues.partiallyComplete'
      );
    case 'SUCCESS':
      return t('logsPortal.importSynchronisationLogs.stateValues.success');
    default:
      return '';
  }
};

export const getImportSyncDateTimeLabel = (
  lastSyncedAt: ImportSyncStatusLogRow['lastSyncedAt']
): string =>
  lastSyncedAt
    ? dayjs(lastSyncedAt).format('DD-MM-YYYY - HH:mm')
    : t('logsPortal.importSynchronisationLogs.neverSynced');

export const useColumnDefinitions =
  (): GridColDef<ImportSyncStatusLogRow>[] => [
    {
      field: 'appointmentDate',
      headerName: t('logsPortal.importSynchronisationLogs.appointmentDate'),
      minWidth: 120,
      sortComparator: (a: dayjs.Dayjs, b: dayjs.Dayjs) => a.diff(b),
      valueGetter: (_, row) =>
        dayjs(row.appointmentContext?.appointmentSummary?.startDateTime),
      valueFormatter: (value: dayjs.Dayjs) => value.format('DD-MM-YYYY')
    },
    {
      field: 'appointmentTime',
      headerName: t('logsPortal.importSynchronisationLogs.appointmentTime'),
      minWidth: 100,
      valueGetter: (_, row) =>
        row.appointmentContext?.appointmentSummary &&
        `${dayjs(
          row.appointmentContext.appointmentSummary?.startDateTime
        ).format('HH:mm')} - ${dayjs(
          row.appointmentContext.appointmentSummary?.endDateTime
        ).format('HH:mm')}`
    },
    {
      field: 'activity',
      headerName: t('logsPortal.importSynchronisationLogs.activity'),
      minWidth: 200,
      valueGetter: (_, row) => {
        const name =
          row.appointmentContext?.appointmentSummary?.activity?.name || '';
        const code = row.appointmentContext?.appointmentSummary?.activity?.code;
        return code ? `${name} (${code})` : name;
      }
    },
    {
      field: 'participants',
      headerName: t('logsPortal.importSynchronisationLogs.participants'),
      minWidth: 300,
      flex: 1,
      valueGetter: (_, row) =>
        row.appointmentContext?.appointmentSummary &&
        getParticipantPreview(row.appointmentContext.appointmentSummary)
    },
    {
      field: 'externalId',
      headerName: t('logsPortal.importSynchronisationLogs.externalId'),
      minWidth: 160,
      valueGetter: (_, row) => row.externalId || ''
    },
    {
      field: 'status',
      headerName: t('logsPortal.importSynchronisationLogs.status'),
      minWidth: 140,
      valueGetter: (_, row) => getImportStatusLabel(row.status)
    },
    {
      field: 'validationMessages',
      headerName: t('logsPortal.importSynchronisationLogs.validationMessages'),
      flex: 1,
      minWidth: 400,
      valueGetter: (_, row) => row.validationMessages || ''
    },
    {
      field: 'syncDateTime',
      headerName: t('logsPortal.importSynchronisationLogs.syncDateTime'),
      minWidth: 170,
      valueGetter: (_, row) => row.lastSyncedAt || null,
      valueFormatter: (_, row) => getImportSyncDateTimeLabel(row.lastSyncedAt)
    }
  ];
